/**
 * Shows what the bot ORIGINALLY heard, on a row where the backend cleaned it up.
 *
 * Why this exists: the phone bot's two free-form slots — the caller's name and the service
 * they asked for — arrive as whole spoken turns, so a name reaches the queue as
 * "my name is number oh no sir". The backend now normalises those before storing them,
 * which makes the queue readable but introduces a worse failure than the one it fixes:
 * a correction that is confidently wrong. Nobody rechecks a name that reads as "Jane
 * Smith", and the workshop rings a stranger.
 *
 * So every correction shows its working. The cleaned value is the headline because it is
 * what staff will use; the raw speech sits under it as the evidence for it.
 *
 * Renders NOTHING when normalisation did not run (rows predating the feature, or with it
 * switched off) or when it ran and changed nothing — a note on every row would be noise,
 * and noise is what stops people reading the rows that matter.
 */

import React from 'react';

/** Fields worth showing, in the order a reader scans the card. */
const FIELDS = [
    { raw: 'rawCustomerName', value: 'customerName', label: 'name' },
    { raw: 'rawVehicleRego', value: 'vehicleRego', label: 'rego' },
    { raw: 'rawSpokenService', value: 'spokenService', label: 'service' },
];

/**
 * The corrections on one row.
 *
 * Exported for its own test: this is the whole decision, and it is easier to be sure it is
 * right here than through two components' markup.
 */
export function corrections(item) {
    if (!item) return [];
    return FIELDS
        .filter(({ raw, value }) => item[raw] && item[raw] !== item[value])
        .map(({ raw, value, label }) => ({ label, heard: item[raw], stored: item[value] }));
}

export default function VoiceCorrectionNote({ item }) {
    const changed = corrections(item);
    if (changed.length === 0) return null;

    return (
        <div className="mt-1.5 rounded-md border border-dashed border-gray-200 px-2 py-1.5">
            <p className="text-[10px] font-medium uppercase tracking-wide text-gray-400">
                Cleaned up — the bot heard
            </p>
            {changed.map(({ label, heard }) => (
                <p key={label} className="text-[11px] text-gray-500">
                    <span className="text-gray-400">{label}:</span> “{heard}”
                </p>
            ))}
        </div>
    );
}
